import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Switch,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MaterialSymbol from '../../components/MaterialSymbol';
import Title from '../../components/Title';
import EditModal from '../../components/EditModal';
import { PRODUCT_NAME, API_ERROR_MESSAGE } from '../../common/AppConstants';
import { formatCurrency } from '../../common/AppUtils';
import { httpRequest, httpRequestUtil } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { useAccount } from '../../context/AccountContext';
import { MANAGE_SUBSCRIPTION_URL } from '../../common/AppUrls';
import logger from '../../logger';


// Razorpay constants
// Convenience Fee: ₹20/- + GST
const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// Org Mandatory Data
const orgColumns = [
  {
    "attr": "name",
    "label": "Name",
    "type": "text",
    "related": false,
    "required": true,
    "editable": false,
    "extra": false,
    "help_text": "Name of the Institute"
  },
  {
    "attr": "country",
    "label": "Country",
    "type": "choice",
    "related": true,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Country"
  },
  {
    "attr": "place_of_supply",
    "label": "State",
    "type": "choice",
    "related": true,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "State"
  },
  {
    "attr": "gstin",
    "label": "GSTIN",
    "type": "text",
    "related": false,
    "required": false,
    "editable": true,
    "extra": false,
    "help_text": "GSTIN of Institute"
  },
];

const STEPS = ['Select Plan', 'Review & Pay'];

const BILLING_CYCLE_LABEL = {
  annual: 'year',
  monthly: 'month',
};

//  API helpers
const ORG_API_URL = 'organization/organization/';
const PLANS_API = "billing/plans/";
const ORDER_API = "billing/create-order/"
const VERIFY_PAYMENT_API = '/billing/verify-payment/';

// Helpers
function computeTotals(variant) {
  const effective = parseFloat(variant.effective_price);
  const taxAmount = effective * parseFloat(variant.tax_rate) / 100;
  return {
    effective,
    taxAmount,
    total: effective + taxAmount,
  };
}

function BillingCycleToggle({ billingCycle, onChange, plans }) {
  // Only show toggle if at least one plan has both cycles
  const hasBothCycles = plans.some(
    (p) => p.variants?.annual && p.variants?.monthly
  );
  if (!hasBothCycles) return null;
 
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
      <Typography
        variant="body2"
        fontWeight={billingCycle === 'monthly' ? 700 : 400}
        color={billingCycle === 'monthly' ? 'text.primary' : 'text.secondary'}
      >
        Monthly
      </Typography>
      <Switch
        checked={billingCycle === 'annual'}
        onChange={(e) => onChange(e.target.checked ? 'annual' : 'monthly')}
        color="success"
      />
      <Typography
        variant="body2"
        fontWeight={billingCycle === 'annual' ? 700 : 400}
        color={billingCycle === 'annual' ? 'text.primary' : 'text.secondary'}
      >
        Annual
      </Typography>
      <Chip label="Save with annual" size="small" color="success" variant="outlined" />
    </Stack>
  );
}

//  Sub-components 

/**
 * Step 1 — Pick a plan
 */

function PlanCard({ plan, billingCycle, isSelected, onSelect }) {
  const variant = plan.variants?.[billingCycle];
 
  // Plan doesn't support this billing cycle
  if (!variant) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', opacity: 0.4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700}>{plan.name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Not available for this billing cycle.
          </Typography>
        </CardContent>
      </Card>
    );
  }
 
  const hasDiscount = parseFloat(variant.discount) > 0;
  const cycleLabel = BILLING_CYCLE_LABEL[billingCycle];
 
  return (
    <Card
      variant="outlined"
      onClick={() => onSelect(plan.id)}
      sx={{
        borderRadius: 2,
        cursor: 'pointer',
        border: '2px solid',
        borderColor: isSelected ? 'success.main' : 'grey.300',
        background: isSelected
          ? 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)'
          : 'background.paper',
        transition: 'all 0.2s ease',
        height: '100%',
        '&:hover': {
          borderColor: isSelected ? 'success.dark' : 'grey.500',
          boxShadow: 2,
        },
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
        {isSelected
          ? <CheckCircleIcon color="success" />
          : <RadioButtonUncheckedIcon sx={{ color: 'grey.400' }} />}
      </Box>
 
      <CardContent sx={{ pr: 5 }}>
        {/* Plan name */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {plan.name}
        </Typography>
 
        {/* Price */}
        <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={800} color="success.dark">
            {formatCurrency(variant.effective_price)}
          </Typography>
          {hasDiscount && (
            <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
              {formatCurrency(variant.price)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            / {cycleLabel}
          </Typography>
        </Stack>
 
        {/* Limits */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {plan.staff_limit > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Up to {plan.staff_limit} teachers</Typography>
            </Stack>
          )}
          {plan.student_limit > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <GroupsOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Up to {plan.student_limit} students</Typography>
            </Stack>
          )}
          {plan.member_limit > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <GroupsOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Up to {plan.member_limit} members</Typography>
            </Stack>
          )}
        </Stack>
 
        {/* Modules */}
        <Divider sx={{ mb: 1.5 }} />
        <Stack spacing={0.5}>
          {plan.modules.map((mod, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2">{mod}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SelectPlanStep({ plans, billingCycle, onBillingCycleChange, selectedPlanId, onSelect, loadingPlans }) {
  if (loadingPlans) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
        ))}
      </Grid>
    );
  }
 
  if (!plans.length) {
    return (
      <Typography color="text.secondary">
        No plans available. Please contact support.
      </Typography>
    );
  }
 
  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Choose a plan
      </Typography>
 
      <BillingCycleToggle
        billingCycle={billingCycle}
        onChange={onBillingCycleChange}
        plans={plans}
      />
 
      <Grid container spacing={2}>
        {plans.map((plan) => (
          <Grid item xs={12} md={6} key={plan.id}>
            <PlanCard
              plan={plan}
              billingCycle={billingCycle}
              isSelected={selectedPlanId === plan.id}
              onSelect={onSelect}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/**
 * Step 3 — Review
 */

function ReviewStep({ selectedPlan, billingCycle }) {
  if (!selectedPlan) return null;
 
  const variant = selectedPlan.variants[billingCycle];
  const { effective, taxAmount, total } = computeTotals(variant);
  const hasDiscount = parseFloat(variant.discount) > 0;
  const cycleLabel = billingCycle === 'annual' ? 'Annually' : 'Monthly';
 
  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Review your subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Confirm the details below before proceeding to payment.
      </Typography>
 
      {/* Plan + limits */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>Plan</Typography>
            <Chip label={cycleLabel} size="small" color="success" variant="outlined" />
          </Stack>
          <Typography variant="body1" fontWeight={500}>{selectedPlan.name}</Typography>
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            {selectedPlan.staff_limit > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2">Up to {selectedPlan.staff_limit} teachers</Typography>
              </Stack>
            )}
            {selectedPlan.student_limit > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <GroupsOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2">Up to {selectedPlan.student_limit} students</Typography>
              </Stack>
            )}
            {selectedPlan.member_limit > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <GroupsOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2">Up to {selectedPlan.member_limit} members</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
 
      {/* Modules */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Included Modules
          </Typography>
          <Stack spacing={0.5}>
            {selectedPlan.modules.map((mod, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2">{mod}</Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
 
      {/* Pricing */}
      <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'success.main',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)' }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Pricing</Typography>
          <Stack spacing={0.5}>
            {hasDiscount && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">List price</Typography>
                <Typography variant="body2" sx={{ textDecoration: 'line-through' }} color="text.disabled">
                  {formatCurrency(variant.price)}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {hasDiscount ? 'After discount' : 'Price'}
              </Typography>
              <Typography variant="body2">{formatCurrency(effective)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">GST ({variant.tax_rate}%)</Typography>
              <Typography variant="body2">{formatCurrency(taxAmount)}</Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="success.dark">
                {formatCurrency(total)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Billed {cycleLabel.toLowerCase()} · GST included
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// ─── Order Summary Sidebar ────────────────────────────────────────────────────
 
function OrderSummarySidebar({ selectedPlan, billingCycle }) {
  if (!selectedPlan) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Select a plan to see pricing.
        </Typography>
      </Card>
    );
  }
 
  const variant = selectedPlan.variants?.[billingCycle];
  if (!variant) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Selected plan is not available for this billing cycle.
        </Typography>
      </Card>
    );
  }
 
  const { effective, taxAmount, total } = computeTotals(variant);
  const hasDiscount = parseFloat(variant.discount) > 0;
  const cycleLabel = billingCycle === 'annual' ? 'Annual' : 'Monthly';
 
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'success.main',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Order Summary</Typography>
 
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={500}>
              {selectedPlan.name} — {cycleLabel}
            </Typography>
            <Typography variant="body2">{formatCurrency(effective)}</Typography>
          </Stack>
          {hasDiscount && (
            <Typography variant="caption" color="success.dark">
              Saving {formatCurrency(variant.discount)}
            </Typography>
          )}
        </Stack>
 
        <Divider sx={{ my: 1.5 }} />
 
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body2">{formatCurrency(effective)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">GST ({variant.tax_rate}%)</Typography>
            <Typography variant="body2">{formatCurrency(taxAmount)}</Typography>
          </Stack>
          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="success.dark">
              {formatCurrency(total)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Billed {cycleLabel.toLowerCase()} · GST included
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
 

//  Main Page 

export default function ManageSubscription() {
  const { addNotification } = useNotification();
  const { account, fetchAccountData } = useAccount();
  const navigate = useNavigate();

  const [orgData, setOrgData] = useState({});
  const [relatedData, setRelatedData] = useState({});
  const [isOrgDataLoading, setIsOrgDataLoading] = useState(true);
  const [isOrgEditModalOpen, setIsOrgEditModalOpen] = useState(false);

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');

  const [activeStep, setActiveStep] = useState(0);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState(null);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;
  const selectedVariant = selectedPlan?.variants?.[billingCycle] ?? null;

  const handleOrgEditModalClose = () => {
    setIsOrgEditModalOpen(false);
  };

  const handleOrgEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: ORG_API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        handleOrgEditModalClose();
        // await fetchAccountData();
        setOrgData(data);
        addNotification('Updated successfully!', 'info');
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const fetchOrgData = async (event) => {
    setIsOrgDataLoading(true);
    await httpRequest({
      method: 'GET',
      url: ORG_API_URL,
      onSuccess: (data) => {
        setRelatedData(data.relatedData);
        setOrgData(data.organization);
      },
      addNotification,
    });
    setIsOrgDataLoading(false);
  };

  useEffect(() => {
    // Provide mandatroy Institute Data
    if (isOrgDataLoading) {
      return;
    }

    if (!orgData.country) {
      console.log("Please provide country");
      addNotification("Please provide country", "info");
      setIsOrgEditModalOpen(true);
    }
    else if (orgData.country === "IN" && !orgData.place_of_supply) {
      console.log("Please provide state and gstin(optional)");
      addNotification("Please provide state and GSTIN (optional)", "info");
      setIsOrgEditModalOpen(true);
    }    
  }, [isOrgDataLoading, orgData]);

  async function fetchPlans() {
    await httpRequest({
      method: 'GET',
      url: PLANS_API,
      onSuccess: (data) => {
        setPlans(data);
        // Should we Add all modules by default ?
        // setSelectedIds();
      },
      onFailure: (data) => {
        setError('Failed to load plans. Please refresh.');
      },
      // addNotification,
    });
    setLoadingPlans(false);
  }

  // Load modules once on mount
  useEffect(() => {
    fetchOrgData();
    fetchPlans();
  }, []);

  // Clear selected plan if it doesn't support the newly chosen billing cycle
  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    if (selectedPlan && !selectedPlan.variants?.[cycle]) {
      setSelectedPlanId(null);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan || !selectedVariant) return;

    setCheckingOut(true);
    setError(null);
    try {
      // Step 1: Create order on backend
      const payload = {
        plan_variant_id: selectedVariant.id,
        place_of_supply_id: orgData.place_of_supply,
      };
      const orderResponse = await httpRequestUtil(
        'POST',
        ORDER_API,
        payload,
      );

      if (orderResponse.status === 200) {
        // Load Razorpay script
        const razorpaySdk = await loadRazorpayScript();
        if (!razorpaySdk) {
          addNotification("Razorpay SDK failed to load", "error");
          return;
        }

        const { order_id, amount } = orderResponse.data;

        // Step 2: Open Razorpay payment window with options
        const razorpay_options = {
          key: razorpayKey,
          order_id: order_id,
          amount: Math.round(parseFloat(amount) * 100),   // Convert to smallest currenct unit - paise
          currency: 'INR',
          name: PRODUCT_NAME,
          description: 'Subscription Payment',
          handler: async function (razorpayResponse) {
            // Step 3: Payment successful - Verify payment on backend
            // console.log(razorpayResponse)
            await httpRequest({
              method: 'POST',
              url: VERIFY_PAYMENT_API,
              data: {
                order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature
              },
              onSuccess: (data) => {
                addNotification('Payment successful!', "success");
                fetchAccountData();
                navigate(MANAGE_SUBSCRIPTION_URL);
              },
              onFailure: (data) => {
                addNotification('Payment verification failed. Contact support.', 'error');
              },
              addNotification,
            });
          },
          prefill: {
            name: account?.user?.first_name,
            email: account?.user?.email,
            contact: account?.user?.phone || "", // Important, otherwise razorpay asks user to manually enter
          },
          theme: {
            color: '#233836'  // #3399cc
          },
          modal: {
            ondismiss: function () {
              setCheckingOut(false);
              addNotification('Payment cancelled by user');
            }
          }
        };

        const razorpay = new window.Razorpay(razorpay_options);
        razorpay.on('payment.failed', function (razorpayResponse) {
          addNotification(`Payment failed: ${razorpayResponse.error.description}`, 'error');
          console.error('Payment failed:', razorpayResponse.error);
        });

        razorpay.open();
      } else {
        if (orderResponse.status === 429) {
          addNotification(API_ERROR_MESSAGE.clientError429, 'warning');
        } else if (orderResponse.status >= 400 && orderResponse.status < 500) {
          if (orderResponse.status === 400 && orderResponse.data.code === "already_subscribed") {
            addNotification( orderResponse.data.detail || 'Already subscribed to premium plan', 'success');
          }
          else if (orderResponse.status === 401) {
            const validation_error =
              orderResponse.data.detail || 'Wrong credentials provided';
            logger.log('Wrong credentials provided', validation_error);
            addNotification(validation_error, 'warning');
          } else {
            addNotification(API_ERROR_MESSAGE.clientError, 'warning');
          }
        } else if (orderResponse.status >= 500) {
          addNotification(API_ERROR_MESSAGE.serverError, 'error');
        } else {
          addNotification(API_ERROR_MESSAGE.error, 'error');
        }
      }
    } catch (error) {
      logger.error('Payment error:', error);
      setError('Checkout failed. Please try again.');
      addNotification('Error initiating payment. Please try again.', 'error');
    } finally {
      setCheckingOut(false);
      // alert(paymentStatus);
    }
  };

  // Step validation
  const canProceed = () => {
    if (activeStep === 0) return selectedPlanId !== null && selectedVariant !== null;
    return true;
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
 
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Title>Subscribe</Title>
        <Typography variant="body1" color="text.secondary">
          Choose the plan that fits you.
        </Typography>
      </Box>
 
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>
 
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
 
      {/* Main layout */}
      <Grid container spacing={3}>
 
        {/* Step content */}
        <Grid item xs={12} md={7}>
          {activeStep === 0 && (
            <SelectPlanStep
              plans={plans}
              billingCycle={billingCycle}
              onBillingCycleChange={handleBillingCycleChange}
              selectedPlanId={selectedPlanId}
              onSelect={setSelectedPlanId}
              loadingPlans={loadingPlans}
            />
          )}
          {activeStep === 1 && (
            <ReviewStep
              selectedPlan={selectedPlan}
              billingCycle={billingCycle}
            />
          )}
        </Grid>
 
        {/* Sidebar */}
        <Grid item xs={12} md={5}>
          <OrderSummarySidebar
            selectedPlan={selectedPlan}
            billingCycle={billingCycle}
          />
        </Grid>
 
      </Grid>
 
      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
        <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
 
        <Stack direction="row" spacing={2} alignItems="center">
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              color="success"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleCheckout}
              disabled={checkingOut || !selectedPlan || !selectedVariant}
              startIcon={
                checkingOut
                  ? <CircularProgress size={16} color="inherit" />
                  : <MaterialSymbol name="crown" size={20} fill={1} />
              }
              sx={{ px: 4, padding: '8px 24px', fontSize: '16px', backgroundColor: '#0e8a0a' }}
            >
              {checkingOut ? 'Processing...' : 'Pay Now'}
            </Button>
          )}
        </Stack>
      </Stack>
 
      {/* Org Edit Modal */}
      <EditModal
        open={isOrgEditModalOpen}
        columns={orgColumns}
        rowData={orgData}
        relatedData={relatedData}
        handleClose={handleOrgEditModalClose}
        handleSave={handleOrgEditSave}
        edit_title="Edit Institute"
      />
 
    </Box>
  );
}
 